import { ENV } from "@/lib/env"
import type { ProductType } from "@/content/products"
import type { TreatmentId } from "@/content/treatments"
import type { ServiceId } from "@/content/services"
import { imageBaseURL } from "@/lib/utils"

export const SITE_NAME = "Biosany"

export const DEFAULT_DESCRIPTION =
	"Productos y tratamientos de bioestimulación celular para aliviar y prevenir padecimientos de forma natural."

export const DEFAULT_OG_IMAGE = "og/inicio.webp"

/** URL absoluta de una página (respeta `VITE_ORIGIN` y el base path en previews). */
export function absolutePageUrl(pathname: string): string {
	const normalized = pathname.startsWith("/") ? pathname.slice(1) : pathname
	return new URL(normalized, ENV.ORIGIN).href
}

/** URL absoluta de un asset en `public/images/`. */
export function absoluteImageUrl(imagePath: string): string {
	return new URL(imageBaseURL(imagePath), ENV.ORIGIN).href
}

/** Slug de producto → nombre de archivo OG (sin extensión). */
const PRODUCT_OG_FILES: Record<ProductType, string> = {
	"shalosh-bayom": "shalosh",
	dermogel: "dermogel",
	taftefet: "taftefet",
	"agua-vita": "vita",
	lazihum: "lazihum",
	nazelet: "nazelet",
	"jabon-neutro": "jabon",
	te: "te"
}

const TREATMENT_OG_OVERRIDES: Partial<Record<TreatmentId, string>> = {
	"diabetes-tipo-2": "og/treatments/diabetes.jpg"
}

export function productOgImagePath(slug: ProductType): string {
	return `og/${PRODUCT_OG_FILES[slug]}.webp`
}

export function treatmentOgImagePath(slug: TreatmentId): string {
	return TREATMENT_OG_OVERRIDES[slug] ?? `og/treatments/${slug}.jpg`
}

export function serviceOgImagePath(id: ServiceId): string {
	return `og/services/${id}.webp`
}

export function formatPageTitle(title: string): string {
	return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`
}

export interface PageSeo {
	title: string
	description: string
	image?: string
}

export const PAGE_SEO = {
	home: {
		title: "Tratamientos de bioestimulación celular",
		description:
			"Productos que alivian y previenen padecimientos crónico-degenerativos en todas las edades, de forma natural.",
		image: DEFAULT_OG_IMAGE
	},
	productos: {
		title: "Productos de bioestimulación celular",
		description:
			"Conoce nuestra línea de productos de bioestimulación celular: soluciones naturales para tu salud y bienestar.",
		image: DEFAULT_OG_IMAGE
	},
	tratamientos: {
		title: "Tratamientos de bioestimulación celular",
		description: "Tratamientos personalizados para aliviar y prevenir padecimientos crónico-degenerativos de forma natural.",
		image: DEFAULT_OG_IMAGE
	},
	servicios: {
		title: "Servicios de bioestimulación celular",
		description: "Dieta personalizada y consulta remota con un especialista en bioestimulación celular.",
		image: DEFAULT_OG_IMAGE
	},
	nosotros: {
		title: "Sobre nosotros",
		description:
			"Conoce Biosany Shalósh Bayóm: más de 20 años de experiencia en bioestimulación celular y productos naturales.",
		image: DEFAULT_OG_IMAGE
	},
	contacto: {
		title: "Contacto",
		description:
			"¿Tienes dudas? Contáctanos por teléfono, WhatsApp o correo. Estamos para ayudarte con tus productos y tratamientos.",
		image: DEFAULT_OG_IMAGE
	},
	condiciones: {
		title: "Términos y condiciones de uso",
		description: "Condiciones de uso, políticas de pago, envíos y política de no devoluciones de Biosany Shalósh Bayóm.",
		image: DEFAULT_OG_IMAGE
	},
	privacidad: {
		title: "Política de privacidad",
		description: "Cómo Biosany recopila, usa y protege tu información personal y de pago.",
		image: DEFAULT_OG_IMAGE
	},
	notFound: {
		title: "Página no encontrada",
		description: "La página que buscas no existe o ya no está disponible.",
		image: DEFAULT_OG_IMAGE
	}
} as const satisfies Record<string, PageSeo>
